import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../../../models/Entities/CashFlow/Payment/Payment';
import { dateCell, ExcelSheet, integer, money } from '../../Contracts/statistics/StatisticsExcel.Functions';
import { currencyOf, daysSince, isPending, Kpis } from './Cashflow.Dashboard.Functions';

/** El libro del flujo de caja: resumen, lo pendiente y los movimientos. */
export const cashflowSheets = (input: {
    kpis: Kpis[];
    debtsAsCreditor: Debt[];
    debtsAsDebtor: Debt[];
    paymentsReceived: Payment[];
    paymentsDone: Payment[];
    nameOf: (identifier: string) => string;
}): ExcelSheet[] => {
    const debtRows = (debts: Debt[], counterpartOf: (debt: Debt) => string) =>
        debts.map((debt) => ({
            Contraparte: input.nameOf(counterpartOf(debt)),
            'Liquidación': debt.settlementId ? `LIQ-${debt.settlementId}` : '',
            Fecha: dateCell(debt.debtDate),
            'Días': integer(daysSince(debt.debtDate)),
            Estado: isPending(debt) ? 'Pendiente' : 'Saldada',
            Moneda: currencyOf(debt),
            Monto: money(debt.originalAmount),
            Pagado: money(debt.paidAmount),
            Pendiente: money(isPending(debt) ? debt.pendingAmount : 0),
            Comentario: debt.comments || ''
        }));

    const paymentRows = (payments: Payment[], counterpartOf: (payment: Payment) => string) =>
        payments.map((payment) => ({
            Contraparte: input.nameOf(counterpartOf(payment)),
            Fecha: dateCell(payment.paymentDate),
            Moneda: currencyOf(payment),
            Monto: money(payment.amount),
            Imputado: money(payment.imputedAmount ?? 0),
            'Sin imputar': money(payment.pendingIndictmentAmount ?? 0),
            Comentario: payment.comments || ''
        }));

    return [
        {
            name: 'Resumen',
            description: 'Indicadores por moneda en el período elegido.',
            rows: input.kpis.map((kpi) => ({
                Moneda: kpi.currency,
                'Por cobrar': money(kpi.receivable),
                'Deudas por cobrar': integer(kpi.receivableCount),
                'Por cobrar con más de 90 días': money(kpi.receivableOverdue),
                'Por pagar': money(kpi.payable),
                'Deudas por pagar': integer(kpi.payableCount),
                'Cobrado en el período': money(kpi.collectedInPeriod),
                'Pagado en el período': money(kpi.paidInPeriod),
                'Saldo neto': money(kpi.net)
            }))
        },
        { name: 'Por cobrar', description: 'Deudas a favor del centro.', rows: debtRows(input.debtsAsCreditor, (debt) => debt.debtor) },
        { name: 'Por pagar', description: 'Deudas del centro con terceros.', rows: debtRows(input.debtsAsDebtor, (debt) => debt.creditor) },
        { name: 'Cobros', description: 'Cobros recibidos por el centro.', rows: paymentRows(input.paymentsReceived, (payment) => payment.payer) },
        { name: 'Pagos', description: 'Pagos hechos por el centro.', rows: paymentRows(input.paymentsDone, (payment) => payment.receiver) }
    ];
};
